import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { storageService } from '@/utils/storage';
import type {
  ChecklistUiState,
  CompanyProgress,
  StepKey,
} from '../config/onboarding.types';
import {
  ONBOARDING_STEPS,
  STALE_AFTER_DAYS,
  TOTAL_MILESTONES,
} from '../config/onboarding.config';

const DAY_MS = 86_400_000;
/** Ventana en que una venta recién detectada todavía cuenta como "la que se acaba de emitir". */
const JUST_DETECTED_MS = 120_000;

/** Id de la cuenta con sesión iniciada (la misma fuente que usa el resto de la app). */
const currentUserId = (): string => storageService.getUser()?.id ?? 'sin-cuenta';
const STEP_KEYS: StepKey[] = ['company', 'product', 'customer'];

const defaultUi = (): ChecklistUiState => ({
  isOpen: false,
  isHidden: false,
  openStepKey: null,
  hasAlert: false,
  welcomeShown: false,
  lastProgressAt: null,
  forcedVisible: false,
});

/**
 * Lee el estado de interfaz de una empresa completando con los valores por
 * defecto. Así el estado persistido por la versión anterior (con openCount,
 * sin lastProgressAt) sigue funcionando sin migración manual.
 */
const uiOf = (ui: Record<string, ChecklistUiState>, companyId: string): ChecklistUiState => ({
  ...defaultUi(),
  ...ui[companyId],
});

/** A quién le corresponde el aviso de primera venta. */
export type FirstSaleNotice = 'modal' | 'toast' | 'none';

interface OnboardingState {
  /** Progreso real, hidratado desde el backend. Clave: companyId. */
  progress: Record<string, CompanyProgress>;
  /** Estado de interfaz, por empresa. Esto sí se persiste. */
  ui: Record<string, ChecklistUiState>;
  /**
   * Cuentas (id de usuario) a las que ya se les mostró la celebración grande.
   * Es de cuenta, no de empresa ni de navegador: dos cuentas en el mismo
   * equipo ven cada una la suya.
   */
  celebrationShownBy: Record<string, boolean>;
  /**
   * Cuenta que emitió pero todavía no vio la celebración (se muestra al volver
   * al listado de comprobantes). null si no hay ninguna pendiente.
   */
  celebrationPendingBy: string | null;
  /**
   * Momento (ms) en que la lectura de progreso detectó por primera vez la
   * venta de la empresa. No se persiste. Sirve para no perder la celebración
   * cuando esa lectura llega justo antes del modal de éxito.
   */
  invoiceDetectedAt: Record<string, number>;
  /** Empresas cuya primera venta ya se atendió en esta sesión. No se persiste. */
  firstSaleHandled: Record<string, boolean>;
  /**
   * Se incrementa para forzar una relectura inmediata del progreso (ver
   * useOnboardingProgress). Crear un cliente o producto desde un modal no
   * cambia de ruta, así que sin esto el checklist quedaría con datos viejos
   * hasta el próximo cambio de pantalla o el siguiente ciclo de refresco.
   */
  refreshSignal: number;
  requestRefresh: () => void;

  // --- hidratación ---
  setProgress: (p: CompanyProgress) => void;

  // --- interfaz ---
  getUi: (companyId: string) => ChecklistUiState;
  openPanel: (companyId: string) => void;
  closePanel: (companyId: string) => void;
  hide: (companyId: string) => void;
  /** Desde Ayuda: lo vuelve a mostrar abierto, aunque la regla por defecto lo oculte. */
  restore: (companyId: string) => void;
  toggleStep: (companyId: string, key: StepKey) => void;
  markWelcomeShown: (companyId: string) => void;

  // --- celebración ---
  /**
   * Se llama en el momento del éxito real de la emisión (no antes). Decide
   * si corresponde el modal grande (primera venta de la cuenta) o solo un
   * aviso breve (empresas siguientes). Es un no-op si esta empresa ya tenía
   * una venta emitida según el último progreso hidratado.
   */
  notifyFirstSale: (companyId: string) => FirstSaleNotice;
  consumeCelebration: () => void;

  // --- derivados ---
  completedCount: (companyId: string) => number;
  remaining: (companyId: string) => number;
  /** Primer paso pendiente, en el orden sugerido. */
  nextStep: (companyId: string) => StepKey | null;
  isReadyToIssue: (companyId: string) => boolean;
  isRetired: (companyId: string) => boolean;
  /** Lleva más de STALE_AFTER_DAYS sin completar ningún paso. */
  isStale: (companyId: string) => boolean;
}

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set, get) => {
      const patchUi = (companyId: string, patch: Partial<ChecklistUiState>) =>
        set((s) => ({
          ui: { ...s.ui, [companyId]: { ...uiOf(s.ui, companyId), ...patch } },
        }));

      return {
        progress: {},
        ui: {},
        celebrationShownBy: {},
        celebrationPendingBy: null,
        invoiceDetectedAt: {},
        firstSaleHandled: {},
        refreshSignal: 0,
        requestRefresh: () => set((s) => ({ refreshSignal: s.refreshSignal + 1 })),

        setProgress: (p) =>
          set((s) => {
            const prev = s.progress[p.companyId];
            const ui = uiOf(s.ui, p.companyId);
            // ¿Se completó algún paso desde la última lectura?
            const advanced = !!prev && STEP_KEYS.some((k) => !prev[k] && p[k]);
            // ¿La venta apareció en esta lectura? (antes no había ninguna)
            const invoiceJustDetected = !!prev && !prev.hasIssuedInvoice && p.hasIssuedInvoice;
            return {
              progress: { ...s.progress, [p.companyId]: p },
              invoiceDetectedAt: invoiceJustDetected
                ? { ...s.invoiceDetectedAt, [p.companyId]: Date.now() }
                : s.invoiceDetectedAt,
              ui: {
                ...s.ui,
                [p.companyId]: {
                  ...ui,
                  // Avance con el popover cerrado: punto de aviso en el lanzador.
                  hasAlert: ui.hasAlert || (advanced && !ui.isOpen),
                  // La primera lectura también inicia el reloj de inactividad.
                  lastProgressAt:
                    advanced || ui.lastProgressAt === null ? Date.now() : ui.lastProgressAt,
                },
              },
            };
          }),

        getUi: (companyId) => uiOf(get().ui, companyId),

        openPanel: (companyId) => patchUi(companyId, { isOpen: true, hasAlert: false }),

        closePanel: (companyId) => patchUi(companyId, { isOpen: false }),

        hide: (companyId) => patchUi(companyId, { isOpen: false, isHidden: true }),

        restore: (companyId) =>
          patchUi(companyId, {
            isHidden: false,
            isOpen: true,
            forcedVisible: true,
            hasAlert: false,
          }),

        toggleStep: (companyId, key) => {
          const ui = uiOf(get().ui, companyId);
          patchUi(companyId, { openStepKey: ui.openStepKey === key ? null : key });
        },

        markWelcomeShown: (companyId) =>
          patchUi(companyId, { welcomeShown: true, isOpen: true }),

        notifyFirstSale: (companyId) => {
          const s = get();
          // Ya se atendió la primera venta de esta empresa en esta sesión.
          if (s.firstSaleHandled[companyId]) return 'none';
          const prev = s.progress[companyId];
          // Sin progreso confirmado: no se puede saber si es un hito nuevo.
          if (!prev) return 'none';
          // Ya tenía una venta, salvo que la lectura la detectara hace instantes:
          // en ese caso es la venta que acaba de emitirse, no una anterior.
          if (prev.hasIssuedInvoice) {
            const detectedAt = s.invoiceDetectedAt[companyId];
            if (detectedAt === undefined || Date.now() - detectedAt > JUST_DETECTED_MS) return 'none';
          }
          const userId = currentUserId();
          set({ firstSaleHandled: { ...s.firstSaleHandled, [companyId]: true } });
          if (s.celebrationShownBy[userId]) return 'toast';
          set({ celebrationPendingBy: userId });
          return 'modal';
        },

        consumeCelebration: () =>
          set((s) =>
            s.celebrationPendingBy
              ? {
                  celebrationPendingBy: null,
                  celebrationShownBy: { ...s.celebrationShownBy, [s.celebrationPendingBy]: true },
                }
              : {},
          ),

        completedCount: (companyId) => {
          const p = get().progress[companyId];
          if (!p) return 0;
          if (p.hasIssuedInvoice) return TOTAL_MILESTONES;
          return [p.company, p.product, p.customer].filter(Boolean).length;
        },

        remaining: (companyId) => TOTAL_MILESTONES - get().completedCount(companyId),

        nextStep: (companyId) => {
          const p = get().progress[companyId];
          if (!p) return null;
          return ONBOARDING_STEPS.find((s) => !p[s.key])?.key ?? null;
        },

        isReadyToIssue: (companyId) => {
          const p = get().progress[companyId];
          return !!p && p.company && p.product && p.customer && !p.hasIssuedInvoice;
        },

        /** El checklist ya no debe mostrarse para esta empresa: ya emitió. */
        isRetired: (companyId) => {
          const p = get().progress[companyId];
          return !!p?.hasIssuedInvoice;
        },

        isStale: (companyId) => {
          const { lastProgressAt } = uiOf(get().ui, companyId);
          return lastProgressAt !== null && Date.now() - lastProgressAt > STALE_AFTER_DAYS * DAY_MS;
        },
      };
    },
    {
      name: 'senciyo-checklist-inicio',
      version: 3,
      // El progreso real viene siempre del backend: no lo persistimos.
      partialize: (s) => ({
        ui: s.ui,
        celebrationShownBy: s.celebrationShownBy,
        celebrationPendingBy: s.celebrationPendingBy,
      }),
      // v1 → v2: los campos nuevos se completan en uiOf().
      // v2 → v3: la celebración pasa de marca única del navegador a marca por cuenta.
      // Las marcas viejas (sin cuenta) se descartan: cada cuenta parte de cero.
      migrate: (persisted) => {
        const { celebrationShown: _shown, celebrationPending: _pending, ...rest } =
          (persisted ?? {}) as Record<string, unknown>;
        return rest as unknown as OnboardingState;
      },
    },
  ),
);
